import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ArrowUpToLine, ArrowDownToLine } from "lucide-react";

interface SortableListProps<T extends { id: number }> {
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  onReorder: (newOrderIds: number[]) => Promise<void> | void;
  showQuickActions?: boolean;
}

export function SortableList<T extends { id: number }>({
  items,
  renderItem,
  onReorder,
  showQuickActions = true,
}: SortableListProps<T>) {
  const [list, setList] = useState(items);
  useEffect(() => setList(items), [items]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  async function commit(next: T[]) {
    setList(next);
    setSaving(true);
    setSaved(false);
    try {
      await onReorder(next.map((x) => x.id));
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
    } catch (e) {
      alert("保存顺序失败：" + (e as Error).message);
      setList(items); // revert
    } finally {
      setSaving(false);
    }
  }

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = list.findIndex((x) => x.id === active.id);
    const newIndex = list.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    commit(arrayMove(list, oldIndex, newIndex));
  }

  function moveToTop(id: number) {
    const idx = list.findIndex((x) => x.id === id);
    if (idx <= 0) return;
    commit(arrayMove(list, idx, 0));
  }

  function moveToBottom(id: number) {
    const idx = list.findIndex((x) => x.id === id);
    if (idx < 0 || idx === list.length - 1) return;
    commit(arrayMove(list, idx, list.length - 1));
  }

  return (
    <div className="relative">
      {(saving || saved) && (
        <div className="absolute right-0 -top-6 text-[11px] text-ink-400">
          {saving ? "保存中…" : "✓ 顺序已保存"}
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={list.map((x) => x.id)} strategy={verticalListSortingStrategy}>
          <div className="divide-y divide-surface-border">
            {list.map((item) => (
              <SortableRow
                key={item.id}
                id={item.id}
                onTop={showQuickActions ? () => moveToTop(item.id) : undefined}
                onBottom={showQuickActions ? () => moveToBottom(item.id) : undefined}
              >
                {renderItem(item)}
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

interface SortableRowProps {
  id: number;
  children: React.ReactNode;
  onTop?: () => void;
  onBottom?: () => void;
}

function SortableRow({ id, children, onTop, onBottom }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "var(--color-surface-muted, #f5f5f5)" : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center">
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab px-2 py-3 text-ink-400 hover:text-ink-900 active:cursor-grabbing"
        aria-label="拖动以重新排序"
        type="button"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
      {(onTop || onBottom) && (
        <div className="flex items-center gap-1 px-2">
          {onTop && (
            <button
              onClick={onTop}
              className="rounded p-1 text-ink-400 hover:bg-surface-muted hover:text-ink-900"
              title="移到顶部"
              type="button"
            >
              <ArrowUpToLine className="h-3.5 w-3.5" />
            </button>
          )}
          {onBottom && (
            <button
              onClick={onBottom}
              className="rounded p-1 text-ink-400 hover:bg-surface-muted hover:text-ink-900"
              title="移到底部"
              type="button"
            >
              <ArrowDownToLine className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
